import * as React from "react";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import { createApi } from "../../../utils/createApi";
import { AxiosError, AxiosResponse } from "axios";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  ResposneObject,
  IgnoreZone,
  SiteCamerasResult,
} from "./SiteAboutPageUtils/siteAboutInterfaces";
import { useNavigate, NavigateFunction } from "react-router-dom";
import { universalAPIErrorHandler } from "../../../utils/universalAPIErrorHandler";
import { resizeImage } from "./SiteAboutPageUtils/resizeImage";
import { addIgnoreLabel } from "./SiteAboutPageUtils/addIgnoreLabel";
import { useSiteAboutStore } from "./siteAboutStore/siteAboutStore";
import { IgnoreZonesTable } from "./IgnoreZonesTable";
import { MetadataTagZonesTable } from "./MetadataTagZonesTable";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlag";
import { MetadataTagZone } from "../../EditIMetadataTagZones/EditMetadataTagZonesUtils/editIMetadataTagZonesInterfaces";
import { pointsCreator } from "./SiteAboutPageUtils/pointsCreator";
import { ACTIVE_PRODUCTS_DISPLAY_NAMES } from "./SiteAboutPageUtils/deriveActiveProductsFromGeneralInfo";
import { cancelIconRed } from "../../../common/cancelIconRed";
import { checkedCircleIcon } from "../../../common/checkedCircleIcon";
import { AutoPatrolFlexPresetSelectForCameraRow } from "./AutoPatrolFlexIgnoreZones";
import { IgnoreZonePreviewHoverLayer } from "../../../common/ignoreZoneHover/IgnoreZonePreviewHoverLayer";

type Dispatcher<S> = React.Dispatch<React.SetStateAction<S>>;

const theme = createTheme({
  palette: {
    primary: {
      main: "#FF8400",
    },
  },
});

export const CameraInfoRowAutoPatrol = ({
  obj,
  expanded,
  lockedScheduleId,
  lockedSchedulePatrolType,
  showSelectionColumn,
  setBackdropLoadingScreenOn,
  siteInfo,
  permissions,
  srcImages,
}: {
  obj: SiteCamerasResult;
  expanded: boolean;
  lockedScheduleId: number | null;
  lockedSchedulePatrolType: string | null;
  showSelectionColumn: boolean;
  setBackdropLoadingScreenOn: Dispatcher<boolean>;
  siteInfo: any;
  permissions: any;
  srcImages: any;
}) => {
  const {
    vCHOnlySchedules,
    activeCameraProducts,
    activeCameraProductsLoading,
    cameraSlice,
    autoPatrolSchedules,
    selectedAutoPatrolScheduleIndex,
    autoPatrolFlexIgnoreZonesEnabledBySite,
  } = useSiteAboutStore();
  const [metadataTagZones, setMetadataTagZones] = React.useState<
    MetadataTagZone[]
  >([]);
  const [allIgnoreZones, setAllIgnoreZones] = React.useState<IgnoreZone[]>([]);
  const [visibleIgnoreZones, setVisibleIgnoreZones] = React.useState<
    IgnoreZone[]
  >([]);
  const [clickedTab, setClickedTab] = React.useState("one");
  const [responseObject, setResponseObject] = React.useState<ResposneObject>({
    // null until /camera/{id}/status/ answers, so an unloaded or failed row
    // shows "No data yet" and not a green tick it never earned (ENG-1782)
    connected: null,
    frame_since_launch: null,
    good_frame: null,
    blur_metric: 0,
    native_frame_rate: 0,
    frame_dimensions: "",
    last_motion: 0,
    preview_url: "",
    health_status: "",
    is_blurry: null,
    aspect_ratio: "",
    comments: "",
    camera_view: "",
    aspect_ratio_status: "unknown",
    frame_rate_status: "unknown",
    resolution_status: "unknown",
    camera_health_comments: "",
    camera_name: "",
    camera_start_time: 0,
    camera_view_comments: "",
    camera_view_status: "unknown",
    codec_status: "unknown",
    display_frame_since_launch: true,
    dynamic_slicing: false,
    id: 0,
    overall_status: "unknown",
  });
  const [dimensions, setDimensions] = React.useState<{
    width: number;
    height: number;
  } | null>(null);
  const [previewLoadFailed, setPreviewLoadFailed] = React.useState(false);
  const [previewRefreshRequested, setPreviewRefreshRequested] = React.useState(false);

  const navigate = useNavigate();
  const { flags } = useFeatureFlag();
  const viewEditMetadataTagZonesPage = flags.viewEditMetadataTagZonesPage;
  const showAutoPatrolFlexIgnoreZones =
    flags.showAutoPatrolFlexIgnoreZones === true;
  const selectedSchedule = autoPatrolSchedules[selectedAutoPatrolScheduleIndex];
  const selectedScheduleId = selectedSchedule?.id;
  const effectiveScheduleId =
    lockedScheduleId != null ? lockedScheduleId : selectedScheduleId ?? null;
  const effectivePatrolType =
    lockedSchedulePatrolType != null
      ? lockedSchedulePatrolType
      : selectedSchedule?.patrolType ?? null;
  const flexIgnoreZonesActive =
    showAutoPatrolFlexIgnoreZones &&
    effectivePatrolType === "AutoPatrol" &&
    !!siteInfo?.id &&
    autoPatrolFlexIgnoreZonesEnabledBySite[siteInfo.id] === true;

  const aPICall = (
    navigate: NavigateFunction,
    id: string,
    obj: SiteCamerasResult,
    setResponseObject: Dispatcher<ResposneObject>,
    setAllIgnoreZones: Dispatcher<IgnoreZone[]>
  ) => {
    const AxiosUI = createApi(``);
    const retryFunction = () => {
      aPICall(
        navigate,
        id,
        obj,
        setResponseObject,
        setAllIgnoreZones
      );
    };
    AxiosUI.get(`/camera/${id}/status/`).then(
      (response: AxiosResponse) => {
        setResponseObject(response.data);
        localStorage.setItem(
          "preview_url",
          JSON.stringify(response.data.preview_url)
        );
        AxiosUI.get(`/camera/${obj.id}/ignore_zones/`).then(
          (response: AxiosResponse) => {
            const normalizedIgnoreZones = addIgnoreLabel(response.data);
            setAllIgnoreZones(normalizedIgnoreZones);
            // In flex preset mode, selected preset zones control the overlay.
            // Avoid overriding them when camera default zones return later.
            if (!flexIgnoreZonesActive) {
              setVisibleIgnoreZones(normalizedIgnoreZones);
            }
            localStorage.setItem(
              "ignoreZones",
              JSON.stringify(normalizedIgnoreZones)
            );
          },
          (reason: AxiosError) => {
            universalAPIErrorHandler(reason, navigate, retryFunction, () => {});
          }
        );
        AxiosUI.get(`tagzone/?camera=${id}`).then(
          (response: AxiosResponse) => {
            setMetadataTagZones(response.data.results);
          },
          (reason: AxiosError) => {
            universalAPIErrorHandler(reason, navigate, retryFunction, () => {});
          }
        );
      },
      (reason: AxiosError) => {
        universalAPIErrorHandler(reason, navigate, retryFunction, () => {});
      }
    );
  };

  React.useEffect(() => {
    if (!expanded || !obj.id) {
      return;
    }
    aPICall(
      navigate,
      obj.id.toString(),
      obj,
      setResponseObject,
      setAllIgnoreZones
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rerun when refreshed row data changes
  }, [expanded, obj.id, obj.has_ignore_zones, obj.preview_url]);

  React.useEffect(() => {
    if (!flexIgnoreZonesActive) {
      setVisibleIgnoreZones(allIgnoreZones);
    }
  }, [flexIgnoreZonesActive, allIgnoreZones]);

  const handleImageLoad = (
    event: React.SyntheticEvent<HTMLImageElement, Event>
  ) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    setDimensions(resizeImage(naturalWidth, naturalHeight));
  };
  const resolvedPreviewUrl = (
    responseObject.preview_url ||
    (obj as any).preview_url ||
    ""
  ).trim();

  const normalizeZonePolygons = React.useCallback((coordinates: unknown): number[][][] => {
    if (!Array.isArray(coordinates) || coordinates.length === 0) {
      return [];
    }
    // API may return either a single polygon (number[][]) or many polygons (number[][][]).
    if (Array.isArray(coordinates[0]) && typeof (coordinates[0] as any[])[0] === "number") {
      return [coordinates as number[][]];
    }
    return coordinates as number[][][];
  }, []);

  const ignoreZoneHoverEnabled =
    clickedTab === "one" &&
    !!dimensions &&
    !previewLoadFailed &&
    !!resolvedPreviewUrl &&
    visibleIgnoreZones.length > 0;

  React.useEffect(() => {
    setPreviewLoadFailed(false);
    setPreviewRefreshRequested(false);
  }, [resolvedPreviewUrl, expanded, obj.id]);

  const refreshPreviewFromApi = React.useCallback(() => {
    if (!obj.id || previewRefreshRequested) {
      return;
    }
    setPreviewRefreshRequested(true);
    const AxiosUI = createApi("");
    AxiosUI.get(`/camera/${obj.id}/status/`)
      .then((response: AxiosResponse) => {
        const nextPreview = (response.data?.preview_url || "").trim();
        setResponseObject((previousState) => ({
          ...previousState,
          ...response.data,
          preview_url: nextPreview || previousState.preview_url,
        }));
        if (nextPreview) {
          setPreviewLoadFailed(false);
        }
      })
      .catch(() => {});
  }, [obj.id, previewRefreshRequested]);

  if (!obj.id) {
    return <></>;
  }

  const showTabs = viewEditMetadataTagZonesPage && !vCHOnlySchedules;

  return (
    <TableRow>
      <TableCell
        style={{ paddingBottom: 0, paddingTop: 0 }}
        colSpan={showSelectionColumn ? 4 : 3}
      >
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "nowrap",
              width: "100%",
              minHeight: 0,
              gap: 1,
              py: 1,
              alignItems: "flex-start",
            }}
          >
            {/* Left – camera photo with zone overlays (maintains aspect ratio) */}
            <Box
              sx={{
                flex: "0 1 50%",
                minWidth: 0,
                maxWidth: showTabs ? "50%" : "100%",
              }}
            >
              {siteInfo.integration_type_name !== "ailink" && (
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      "& > img": { display: "block" },
                    }}
                  >
                    <IgnoreZonePreviewHoverLayer
                      zones={visibleIgnoreZones}
                      enabled={ignoreZoneHoverEnabled}
                    >
                    {!previewLoadFailed && resolvedPreviewUrl && (
                      <img
                        src={resolvedPreviewUrl}
                        style={{
                          width: "100%",
                          height: "auto",
                          maxWidth: "100%",
                          verticalAlign: "middle",
                        }}
                        onLoad={handleImageLoad}
                        onError={() => {
                          setPreviewLoadFailed(true);
                          refreshPreviewFromApi();
                        }}
                        alt="Camera preview"
                      />
                    )}
                    {(previewLoadFailed || !resolvedPreviewUrl) && (
                      <p className="font-mulish text-[14px] text-gray-600">
                        Camera preview unavailable.
                      </p>
                    )}
                    {dimensions &&
                      !previewLoadFailed &&
                      !!resolvedPreviewUrl &&
                      clickedTab === "one" &&
                      visibleIgnoreZones.flatMap((object: IgnoreZone, zoneIndex: number) =>
                        normalizeZonePolygons(object.coordinates).map((polygon, polygonIndex) => (
                          <svg
                            key={`ignore-${obj.id}-${zoneIndex}-${polygonIndex}-${object.stream_id}-${object.metric_id}-${object.label}`}
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              pointerEvents: "none",
                            }}
                            viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
                            preserveAspectRatio="xMidYMid meet"
                          >
                            <polygon
                              points={pointsCreator(
                                polygon,
                                dimensions.height,
                                dimensions.width
                              )}
                              stroke={object.line_color}
                              strokeWidth="5"
                              fill={object.line_color}
                              fillOpacity="50%"
                            />
                          </svg>
                        ))
                      )}
                    {dimensions &&
                      !previewLoadFailed &&
                      !!resolvedPreviewUrl &&
                      clickedTab === "two" &&
                      metadataTagZones.map(
                        (object: MetadataTagZone, index: number) => (
                          <svg
                            key={
                              object.id ??
                              `metadata-tag-${object.camera}-${object.name}-${index}`
                            }
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              pointerEvents: "none",
                            }}
                            viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
                            preserveAspectRatio="xMidYMid meet"
                          >
                            {object.coordinates.map(
                              (polygon: number[][], index: number) => (
                                <polygon
                                  key={index}
                                  points={pointsCreator(
                                    polygon,
                                    dimensions.height,
                                    dimensions.width
                                  )}
                                  stroke={object.line_color}
                                  strokeWidth="5"
                                  fill={object.line_color}
                                  fillOpacity="50%"
                                />
                              )
                            )}
                          </svg>
                        )
                      )}
                    </IgnoreZonePreviewHoverLayer>
                  </Box>
                )}
              {siteInfo?.integration_type_name === "autopatrol" && (
                <Box sx={{ mt: 1.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontFamily: "Mulish",
                        fontWeight: 600,
                        fontSize: { xs: "12px", sm: "13px" },
                      }}
                    >
                      Slicing
                    </Box>
                    {cameraSlice[obj.id] === undefined ? (
                      <Box component="span" sx={{ color: "text.secondary" }}>
                        —
                      </Box>
                    ) : cameraSlice[obj.id] ? (
                      checkedCircleIcon
                    ) : (
                      cancelIconRed
                    )}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      fontFamily: "Mulish",
                      fontWeight: 600,
                      fontSize: { xs: "12px", sm: "13px" },
                      display: "block",
                      mb: 0.5,
                    }}
                  >
                    Active Products
                  </Box>
                  {activeCameraProductsLoading[obj.id] ? (
                    <CircularProgress size={18} color="primary" />
                  ) : activeCameraProducts[obj.id] === undefined ? (
                    <Box
                      component="span"
                      sx={{
                        fontFamily: "Mulish",
                        fontSize: { xs: "12px", sm: "14px" },
                        color: "text.secondary",
                      }}
                    >
                      —
                    </Box>
                  ) : (
                    <>
                      {ACTIVE_PRODUCTS_DISPLAY_NAMES.filter(
                        (name) => activeCameraProducts[obj.id]?.[name]
                      ).length === 0 ? (
                        <Box
                          component="span"
                          sx={{
                            fontFamily: "Mulish",
                            fontSize: { xs: "12px", sm: "14px" },
                            color: "text.secondary",
                          }}
                        >
                          —
                        </Box>
                      ) : (
                        <Box
                          component="span"
                          sx={{
                            fontFamily: "Mulish",
                            fontSize: { xs: "12px", sm: "14px" },
                          }}
                        >
                          {ACTIVE_PRODUCTS_DISPLAY_NAMES.filter(
                            (name) => activeCameraProducts[obj.id]?.[name]
                          ).join(", ")}
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              )}
            </Box>

            {/* Right – Ignore Zones / Metadata Tag Zones tabs (always to the right of image) */}
            {showTabs && (
              <Box
                sx={{
                  flex: "0 1 50%",
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <ThemeProvider theme={theme}>
                    <Tabs
                      value={clickedTab}
                      onChange={(event, tabValue) => {
                        setClickedTab(tabValue);
                      }}
                      aria-label="zones tabs"
                      sx={{
                        minHeight: 36,
                        "& .MuiTab-root": {
                          minHeight: 36,
                          fontFamily: "Mulish",
                          fontWeight: "Bold",
                          fontSize: { xs: "11px", sm: "12px", md: "13px" },
                        },
                      }}
                    >
                      <Tab value="one" label="Ignore Zones" />
                      <Tab value="two" label="Metadata Tag Zones" />
                    </Tabs>
                  </ThemeProvider>
                </Box>
                <Box sx={{ overflow: "auto", flex: 1, minHeight: 0, paddingTop: "10px" }}>
                  {clickedTab === "one" && (
                    <>
                      {flexIgnoreZonesActive &&
                        effectiveScheduleId != null &&
                        Number.isFinite(Number(effectiveScheduleId)) && (
                          <AutoPatrolFlexPresetSelectForCameraRow
                            siteId={Number(siteInfo.id)}
                            scheduleId={Number(effectiveScheduleId)}
                            cameraId={Number(obj.id)}
                            cameraName={obj.camera_name}
                            previewUrl={resolvedPreviewUrl}
                            defaultZones={allIgnoreZones}
                            onZonesChange={(zones) => setVisibleIgnoreZones(zones)}
                          />
                        )}
                      <IgnoreZonesTable
                        visibleIgnoreZones={visibleIgnoreZones}
                        onEditClick={() => {
                          setBackdropLoadingScreenOn(true);
                          navigate(
                            `/sites/${siteInfo.id}/editcamera/${obj.id}/editignorezones`
                          );
                        }}
                      />
                    </>
                  )}
                  {clickedTab === "two" && (
                    <MetadataTagZonesTable
                      metadataTagZones={metadataTagZones}
                      onEditClick={() => {
                        setBackdropLoadingScreenOn(true);
                        navigate(
                          `/sites/${siteInfo.id}/editcamera/${obj.id}/editmetadatatagzones`
                        );
                      }}
                    />
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
};
